import {
  Box,
  Card,
  Typography,
  TableHead,
  CardMedia,
  Tooltip,
  IconButton,
  lighten,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Table,
  TableContainer,
  styled,
  useTheme,
  Tab
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import TrendingDownTwoToneIcon from '@mui/icons-material/TrendingDownTwoTone';
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone';
import TrendingFlatTwoToneIcon from '@mui/icons-material/TrendingFlatTwoTone';
import Text from 'src/components/Text';
import { format, subDays } from 'date-fns';
import moment from 'moment';
import { BatchReportResponse } from 'src/services/apiService/response-models/report';

const CardActions = styled(Box)(
  ({ theme }) => `
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
    z-index: 7;
  `
);

const LabelInfo = styled(Box)(
  ({ theme }) => `
    display: inline-block;
    background: ${theme.palette.info.main};
    color: ${theme.palette.info.contrastText};
    text-transform: uppercase;
    font-size: ${theme.typography.pxToRem(10)};
    font-weight: bold;
    line-height: 23px;
    height: 22px;
    padding: ${theme.spacing(0, 2)};
    border-radius: ${theme.general.borderRadiusSm};
  `
);

const TableHeadWrapper = styled(TableHead)(
  ({ theme }) => `
      .MuiTableCell-root {
          text-transform: none;
          font-size: ${theme.typography.pxToRem(15)};
          padding-top: 0;
          padding-bottom: ${theme.spacing(1)};
          color: ${theme.colors.alpha.black[100]};
      }

      .MuiTableRow-root {
          background: transparent;
      }
  `
);

const TableWrapper = styled(Table)(
  () => `
    .MuiTableCell-root {
        border-bottom: 0;
    }
  `
);

const IconButtonWrapper = styled(IconButton)(
  ({ theme }) => `
    transition: ${theme.transitions.create(['transform', 'background'])};
    transform: scale(1);
    transform-origin: center;

    &:hover {
        transform: scale(1.1);
    }
  `
);

const DotLegend = styled('span')(
  ({ theme }) => `
      border-radius: 22px;
      width: 10px;
      height: 10px;
      display: inline-block;
      margin-right: ${theme.spacing(0.5)};
  `
);

interface Transaction {
  appName?: string;
  appRegisterId: string;
  employee?: { name?: string; id?: string };
  batchNumber?: string;
  transactionType?: string;
  remark?: string;
  note: string;
  createdOn: Date;
  tenantCode?: string;
  amount?: number;
}

interface BatchTransactionProps {
  batchSummary: BatchReportResponse[] | null | undefined;
}

function BatchTransaction({ batchSummary }: BatchTransactionProps) {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();

  // Use batchSummary as the list of batch records
  const batches = batchSummary || [];

  return (
    <Card>
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="222"
          image="/static/images/Dashboard/header1.png"
          alt="..."
        />
        <CardActions>
          <LabelInfo sx={{ borderRadius: 50 }}>{t('Batch Summary')}</LabelInfo>
        </CardActions>
      </Box>
      <Box p={2}>
        <TableContainer>
          <TableWrapper>
            <TableHeadWrapper>
              <TableRow>
                <TableCell>{t('Start Time')}</TableCell>
                <TableCell>{t('End Time')}</TableCell>
                <TableCell>{t('App Name')}</TableCell>
                <TableCell>{t('Batch Number')}</TableCell>
                <TableCell>{t('Employee')}</TableCell>
                <TableCell>{t('Status')}</TableCell>
                <TableCell>{t('Order Count')}</TableCell>
                <TableCell>{t('Total')}</TableCell>
              </TableRow>
            </TableHeadWrapper>
            <TableBody>
              {batches.length > 0 ? (
                batches.map((batch, idx) => (
                  <TableRow hover key={batch.batchId || idx}>
                    <TableCell>
                      {batch.startAt ? moment(batch.startAt).format('YYYY-MM-DD HH:mm:ss') : '--'}
                    </TableCell>
                    <TableCell>
                      {batch.endAt ? moment(batch.endAt).format('YYYY-MM-DD HH:mm:ss') : '--'}
                    </TableCell>
                    <TableCell>{batch.appName || '--'}</TableCell>
                    <TableCell>{batch.batchNumber || '--'}</TableCell>
                    <TableCell>{batch.employee?.name || '--'}</TableCell>
                    <TableCell>{batch.status || '--'}</TableCell>
                    <TableCell>{batch.orderCount !== undefined ? batch.orderCount : '--'}</TableCell>
                    <TableCell>{batch.total !== undefined ? batch.total : '--'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {t('No batch records found')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </TableWrapper>
        </TableContainer>
      </Box>
    </Card>
  );
}

export default BatchTransaction;
